window.Theme = window.Theme || {};


Theme.querySelectorAll = function (selector, context = document) {
  return Array.from(context.querySelectorAll(selector));
};


Theme.URLParams = {
  set: function (key, value) {
    const url = new URL(window.location.href);
    url.searchParams.set(key, value);
    history.replaceState(null, '', url);
  },

  get: function (key) {
    const url = new URL(window.location.href);
    return url.searchParams.get(key);
  },

  delete: function (key) {
    const url = new URL(window.location.href);
    url.searchParams.delete(key);
    history.replaceState(null, '', url);
  },

  has: function (key) {
    const url = new URL(window.location.href);
    return url.searchParams.has(key);
  }
};


Theme.Helpers = {
  preload: function (url) {
    if (!url) {
      return;
    }

    const img = new Image();
    img.src = url;
  },

  prefetch: function (url) {
    if (document.querySelector(`link[href="${url}"]`)) {
      return;
    }

    const link = document.createElement("link");

    link.rel = "prefetch";
    link.href = url;

    document.head.appendChild(link);
  },

  debounce: function (fn, delay) {
    let timeout;

    return function() {
      const context = this;
      const args = arguments;

      clearTimeout(timeout);

      timeout = setTimeout(() => {
        fn.apply(context, args);
      }, delay);
    };
  },

  throttle: function (callback, limit) {
    let lastCall = 0;

    return function () {
      const now = Date.now();

      if (now - lastCall >= limit) {
        lastCall = now;
        callback.apply(this, arguments);
      }
    };
  },

  scroll_to: function (id, offset = 80) {
    const target = document.getElementById(id);

    if (!target) {
      return;
    }

    const position = target.offsetTop - offset;

    window.scrollTo({
      top: position,
      behavior: "smooth"
    });
  }
};


Theme.Component = class extends HTMLElement {
  #elements = {};
  #events = {};
  #data = null;
  #controller = null;

  constructor() {
    super();

    this.elements = {};
  }

  get data() {
    if (this.#data != null) {
      return this.#data;
    }

    const element = this.querySelector(":scope > script[type='application/json']");

    if (!element) {
      return (this.#data = {});
    } else {
      return (this.#data = JSON.parse(element.textContent));
    }
  }

  defineElements(elements) {
    for (const [name, selector] of Object.entries(elements)) {
      this.defineElement(name, selector);
    }
  }

  defineElementsAll(elements) {
    for (const [name, selector] of Object.entries(elements)) {
      this.defineElementAll(name, selector);
    }
  }

  defineElement(name, selector) {
    Object.defineProperty(this.elements, name, {
      get: () => {
        return this.#elements[name] || (this.#elements[name] = this.querySelector(selector));
      }
    });
  }

  defineElementAll(name, selector) {
    Object.defineProperty(this.elements, name, {
      get: () => {
        return this.#elements[name] || (this.#elements[name] = Theme.querySelectorAll(selector, this));
      }
    });
  }

  events() {
    this.#controller = new AbortController();
    this.#events = {};
    this.addActionAttributeEvents();
    this.setupEventDelegation();
  }

  addActionAttributeEvents() {
    const elements = this.querySelectorAll("[data-action]");

    elements.forEach((element) => {
      const [event, method] = element.dataset.action.split(":");
      const detail = JSON.parse(element.dataset.detail || "{}");

      this.addEvent(event, element, () => {
        this[method](detail, element);
      });
    });
  }

  addEvent(event, element, fn) {
    this.#events[event] ||= [];
    this.#events[event].push({ element, fn });
  }

  setupEventDelegation() {
    for (const [event, handlers] of Object.entries(this.#events)) {
      this.addEventListener(event, (e) => {
        handlers.forEach((handler) => {
          if (this.closestElement(e.target, handler.element)) {
            e.preventDefault();
            handler.fn.apply(this);
          }
        });
      }, { signal: this.#controller.signal });
    }
  }

  closestElement(starting, matching) {
    let current = starting;

    while (current) {
      if (current === matching) {
        return current;
      }

      current = current.parentElement;
    }

    return null;
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }
}


Theme.ComponentCollection = class {
  #elements = null;

  constructor(selector) {
    this.define(selector);
  }

  define(selector) {
    Object.defineProperty(this, "elements", {
      get: () => {
        return this.#elements || (this.#elements = Theme.querySelectorAll(selector));
      }
    });
  }
};

Theme.ComponentCollections = {};
